const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Whole calendar days from `date` to `now` (0 = today). */
const daysBetween = (date, now) =>
  Math.round((startOfDay(now).getTime() - startOfDay(date).getTime()) / DAY_MS);

const monthsBetween = (date, now) =>
  (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());

/**
 * The heading an activity falls under, as an i18n key plus the count it needs.
 *
 * Returns a KEY, not a string: the util stays free of translation and locale so
 * the card and the list own their copy, and a test can pin the bucketing
 * without a translator. Buckets, newest first: Today, Yesterday, this week,
 * this month, then calendar months ago ("A month ago", "2 months ago"…) and
 * years ago past twelve.
 *
 * @param {string|Date} occurredAt
 * @param {Date} [now]
 * @returns {{ id: string, labelKey: string, count?: number }}
 */
export const activityGroupFor = (occurredAt, now = new Date()) => {
  // The mapper stores an absent timestamp as `null`, and `new Date(null)` is the
  // Unix epoch (not an Invalid Date), so it would land under "N years ago"
  // instead of "unknown" — guard it before parsing.
  if (occurredAt == null || occurredAt === '') {
    return { id: 'unknown', labelKey: 'activity.groups.unknown' };
  }
  const date = new Date(occurredAt);
  if (Number.isNaN(date.getTime())) return { id: 'unknown', labelKey: 'activity.groups.unknown' };

  const days = daysBetween(date, now);
  if (days <= 0) return { id: 'today', labelKey: 'activity.groups.today' };
  if (days === 1) return { id: 'yesterday', labelKey: 'activity.groups.yesterday' };
  if (days < 7) return { id: 'this-week', labelKey: 'activity.groups.thisWeek' };

  const months = monthsBetween(date, now);
  if (months <= 0) return { id: 'this-month', labelKey: 'activity.groups.thisMonth' };
  if (months === 1) return { id: 'months-1', labelKey: 'activity.groups.aMonthAgo' };
  if (months < 12) {
    return { id: `months-${months}`, labelKey: 'activity.groups.monthsAgo', count: months };
  }

  const years = Math.floor(months / 12);
  return years === 1
    ? { id: 'years-1', labelKey: 'activity.groups.aYearAgo' }
    : { id: `years-${years}`, labelKey: 'activity.groups.yearsAgo', count: years };
};

/**
 * Group a newest-first feed under its date headings, preserving order.
 *
 * Grouping is by the heading, not by the day, so two activities that read
 * "A month ago" share one heading however many days apart they are. Consecutive
 * runs only: the list is already sorted, so a heading can never repeat.
 *
 * @template T
 * @param {T[]} items — each with `occurredAt`
 * @param {Date} [now]
 * @returns {{ id: string, labelKey: string, count?: number, items: T[] }[]}
 */
export const groupActivityByDate = (items, now = new Date()) => {
  const groups = [];

  items.forEach((item) => {
    const group = activityGroupFor(item.occurredAt, now);
    const last = groups[groups.length - 1];

    if (last && last.id === group.id) last.items.push(item);
    else groups.push({ ...group, items: [item] });
  });

  return groups;
};

/**
 * "Today, 08:12"-style stamp for a card. Locale-aware through `Intl`, so the
 * month names and the 12/24h clock follow the app language with no key per
 * month.
 */
export const formatActivityTimestamp = (occurredAt, locale) => {
  if (occurredAt == null || occurredAt === '') return '';
  const date = new Date(occurredAt);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};
